import {
  RiskScoreWindow,
  type GetRiskMetricGroupCommandInput,
  type RiskMetric,
} from '@amzn/gbsa-helios-typescript-client';

import { RISK_ENGINE_DEFAULT_WINDOW } from './riskScoreRequest';

interface BuildRiskMetricGroupRequestOptions {
  readonly businessUnitId: string | undefined;
  readonly location: string;
  readonly metricGroupId: string;
  readonly timeWindow?: RiskScoreWindow | undefined;
}

/** Builds the drill-down request for one station's metric group on the live scoring day. */
export function buildRiskMetricGroupRequest({
  businessUnitId,
  location,
  metricGroupId,
  timeWindow = RISK_ENGINE_DEFAULT_WINDOW,
}: BuildRiskMetricGroupRequestOptions): GetRiskMetricGroupCommandInput {
  return {
    businessUnitId: businessUnitId ?? '',
    location,
    metricGroupId,
    timeWindow,
  };
}

export interface RiskBar {
  /**
   * The metric's actual as a percentage of its target — the true ratio, so it may exceed 100 (over
   * target) or be negative. Shown as the numeric label. Deliberately direction-blind: whether a
   * value above or below 100 is good is carried by the metric's status colour, not this number (a
   * "lower is better" metric at 547% is bad, and its red status says so).
   */
  readonly percentOfTarget: number;
  /** `percentOfTarget` clamped to 0–100 for the ProgressBar fill — the bar pegs full past target. */
  readonly fill: number;
}

/**
 * Positions a metric against its target on a single 0–100 axis where the target is 100%: the bar
 * fills to actual/target and pegs full once actual reaches target, while `percentOfTarget` keeps the
 * true figure for the label so an over-target value still reads (e.g. 547%) even though the bar is
 * capped. Direction-agnostic by design — the status colour (applied by the caller) says whether
 * over/under target is good. Absent when either value is missing or the target is non-positive.
 */
export function riskBar(metric: RiskMetric): RiskBar | undefined {
  const { actualValue, targetValue } = metric;
  if (actualValue === undefined || targetValue === undefined) return undefined;
  if (targetValue <= 0) return undefined;
  const percentOfTarget = (actualValue / targetValue) * 100;
  const fill = Math.max(0, Math.min(100, percentOfTarget));
  return { percentOfTarget, fill };
}

/** Orders a sibling group for display. */
type MetricComparator = (a: RiskMetric, b: RiskMetric) => number;

/** Bigger contributors first; metrics the source left unscored sort to the end. */
function compareByContribution(a: RiskMetric, b: RiskMetric): number {
  const aPct = a.unsignedContributionToParentPct;
  const bPct = b.unsignedContributionToParentPct;
  if (aPct === undefined && bPct === undefined) return 0;
  if (aPct === undefined) return 1;
  if (bPct === undefined) return -1;
  return bPct - aPct;
}

/**
 * Row order within a sibling group: hurting drivers first, helping drivers last — i.e. "critical /
 * negative impact" at the top, "healthy / positive impact" at the bottom. Keyed on the signed SHAP
 * contribution, descending: a positive SHAP raises the parent's risk (hurting) and a negative one
 * lowers it (helping), so the biggest problem leads and the strongest helper trails, and a small
 * hurter still outranks a large helper. Scored drivers sort ahead of unscored ones; a pair with no
 * SHAP falls back to the unsigned-contribution magnitude so behaviour is unchanged for groups the
 * source scored without SHAP.
 */
function compareByImpact(a: RiskMetric, b: RiskMetric): number {
  const aShap = a.shapContributionToParent;
  const bShap = b.shapContributionToParent;
  if (aShap !== undefined && bShap !== undefined) return bShap - aShap;
  if (aShap !== undefined) return -1;
  if (bShap !== undefined) return 1;
  return compareByContribution(a, b);
}

/** One metric in the rebuilt group hierarchy: the metric, its depth (root = 0) and ranked children. */
export interface RiskMetricNode {
  readonly metric: RiskMetric;
  readonly depth: number;
  readonly children: readonly RiskMetricNode[];
}

/** The deepest driver level the drill-down renders below the root KPI. */
export const RISK_METRIC_TREE_MAX_DEPTH = 4;

const EMPTY_NODES: readonly RiskMetricNode[] = [];

/**
 * Rebuilds the group hierarchy the service returns flat — one `isRoot` metric plus descendants that
 * each name their `parentMetric` — into a tree rooted at the KPI. The root is depth 0; drivers are
 * depth 1..`maxDepth` and anything deeper is pruned (the drill-down shows at most `maxDepth` driver
 * levels). Each level is ranked by `compare`, which defaults to impact order (hurting first) for the
 * breakdown tree; callers wanting a different order (e.g. the one-level drill-down's contribution
 * order) pass their own. A `visited` set guards against a malformed group whose `parentMetric` links
 * revisit an ancestor. Absent when the group has no root (e.g. no data). Orphans — descendants whose
 * parent is not reachable from the root — are left out.
 */
export function buildMetricTree(
  metrics: readonly RiskMetric[],
  maxDepth: number = RISK_METRIC_TREE_MAX_DEPTH,
  compare: MetricComparator = compareByImpact,
): RiskMetricNode | undefined {
  const root = metrics.find((metric) => metric.isRoot);
  if (!root) return undefined;

  const childrenByParent = new Map<string, RiskMetric[]>();
  for (const metric of metrics) {
    if (metric.isRoot || metric.parentMetric === undefined) continue;
    const bucket = childrenByParent.get(metric.parentMetric);
    if (bucket) bucket.push(metric);
    else childrenByParent.set(metric.parentMetric, [metric]);
  }

  const visited = new Set<string>();
  const build = (metric: RiskMetric, depth: number): RiskMetricNode => {
    visited.add(metric.metric);
    if (depth >= maxDepth) return { metric, depth, children: EMPTY_NODES };
    const children = (childrenByParent.get(metric.metric) ?? [])
      .filter((child) => !visited.has(child.metric))
      .sort(compare)
      .map((child) => build(child, depth + 1));
    return { metric, depth, children };
  };

  return build(root, 0);
}

/**
 * The root metric's immediate sub-metric drivers, biggest contributor first — the one-level view
 * used by the in-table drill-down. A thin projection of {@link buildMetricTree} capped at the first
 * level, so the hierarchy logic lives in one place. Deliberately keeps the historical
 * contribution-magnitude order (`compareByContribution`), NOT the breakdown tree's impact order, so
 * this existing surface is unaffected by the breakdown page's hurting-first sort. Empty when the
 * group has no root or the root has no direct children.
 */
export function selectRootDrivers(metrics: readonly RiskMetric[]): RiskMetric[] {
  return (
    buildMetricTree(metrics, 1, compareByContribution)?.children ?? EMPTY_NODES
  ).map((node) => node.metric);
}

/** One visible row in the drill-down table: a tree node plus whether it can be expanded. */
export interface FlatMetricRow {
  readonly node: RiskMetricNode;
  readonly hasChildren: boolean;
}

/**
 * Flattens a forest of tree nodes into the ordered rows a drill-down table should render for a given
 * expanded-id set: every node is listed in depth-first order, and a node's children follow it only
 * when that node's id (its metric code) is in `expandedIds`. Collapsing a node therefore hides its
 * whole subtree. Node depth is carried on each node for indentation.
 */
export function flattenVisibleRows(
  roots: readonly RiskMetricNode[],
  expandedIds: ReadonlySet<string>,
): FlatMetricRow[] {
  const rows: FlatMetricRow[] = [];
  const walk = (nodes: readonly RiskMetricNode[]): void => {
    for (const node of nodes) {
      const hasChildren = node.children.length > 0;
      rows.push({ node, hasChildren });
      if (hasChildren && expandedIds.has(node.metric.metric)) {
        walk(node.children);
      }
    }
  };
  walk(roots);
  return rows;
}

/** The impact directions, declared once; the type is derived so the strings live in one place. */
export const IMPACT_DIRECTION = {
  helping: 'helping',
  hurting: 'hurting',
  neutral: 'neutral',
} as const;

/** Which way a driver pushes its parent, from the sign of its SHAP contribution. */
export type ImpactDirection = (typeof IMPACT_DIRECTION)[keyof typeof IMPACT_DIRECTION];

export interface ImpactShare {
  /**
   * The Andes-computed `unsignedContributionToParentPct` read directly from the API response.
   * Represents the driver's contribution to its parent's movement, 0–100.
   */
  readonly sharePct: number;
  /** From the sign of the raw SHAP: positive raises the parent's risk (hurting), negative helps. */
  readonly direction: ImpactDirection;
}

/**
 * Builds the display "impact share" for every node in the forest, keyed by metric code.
 *
 * `sharePct` is the Andes-computed `unsignedContributionToParentPct` from the API response —
 * derived from full-precision SHAP values before rounding, so it is more accurate than a
 * frontend re-normalisation of the rounded `shapContributionToParent` values.
 * `direction` is still derived from the sign of `shapContributionToParent` (positive = hurting,
 * negative = helping); when that field is absent the direction defaults to neutral.
 *
 * A node is omitted from the map (shows a dash in the table) when
 * `unsignedContributionToParentPct` is absent.
 */
export function buildImpactShareMap(
  roots: readonly RiskMetricNode[],
): Map<string, ImpactShare> {
  const shares = new Map<string, ImpactShare>();
  const walk = (nodes: readonly RiskMetricNode[]): void => {
    for (const node of nodes) {
      const pct = node.metric.unsignedContributionToParentPct;
      if (pct !== undefined) {
        const shap = node.metric.shapContributionToParent;
        shares.set(node.metric.metric, {
          sharePct: pct,
          direction:
            shap === undefined || shap === 0
              ? IMPACT_DIRECTION.neutral
              : shap > 0
                ? IMPACT_DIRECTION.hurting
                : IMPACT_DIRECTION.helping,
        });
      }
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(roots);
  return shares;
}
